import { Anchor, Stack, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { TextSerif } from "@/components/elements";
import { CreateGarageForm } from "@/components/forms";
import { global_settings } from "@/config";
import { useCreateGarage } from "@/services/garages/create-garage";
import { showCustomNotification } from "@/utils/notifications";

import type { CreateGarageInput } from "@/services/garages/create-garage";
import type { Garage } from "@/types/garage";
import type { MantineStyleProps } from "@mantine/core";
import type { FC } from "react";

export type CreateGarageProps = {
  autoCloseDelay?: boolean | number;
  onAction?: () => void;
  onClose?: () => void;
  onSuccess?: (garageId: string) => void;
} & MantineStyleProps;

export const CreateGarage: FC<CreateGarageProps> = ({
  autoCloseDelay = false,
  onAction,
  onClose,
  onSuccess,
  ...props
}) => {
  const { t } = useTranslation();
  const { mutate, isPending } = useCreateGarage({
    mutationConfig: { onSuccess: handleSuccess, onError: handleError },
  });

  function handleSuccess(data: Garage) {
    const { hide } = showCustomNotification({
      title: t("components.CreateGarage.success.title"),
      message: t("components.CreateGarage.success.message"),
      actionButton: onSuccess
        ? {
            text: t("components.CreateGarage.success.action"),
            action: () => {
              onSuccess(data.identifier);
              hide();
            },
          }
        : undefined,
      autoClose: autoCloseDelay,
    });
    onClose?.();
  }

  function handleError() {
    showCustomNotification({
      title: t("components.CreateGarageForm.error.title"),
      message: t("components.CreateGarageForm.error.message"),
      color: "red",
      autoClose: autoCloseDelay,
    });
  }

  function handleSubmit(data: CreateGarageInput) {
    mutate({ data });
  }

  return (
    <Stack {...props}>
      <Text>
        <TextSerif component="span">{t("components.CreateGarage.description")}</TextSerif>
        {onAction && global_settings.enablePayments && (
          <>
            {" "}
            <Anchor fw="bold" onClick={onAction}>
              {t("components.CreateGarage.action")}
            </Anchor>
          </>
        )}
      </Text>
      <CreateGarageForm loading={isPending} onCancel={onClose} onSubmit={handleSubmit} />
    </Stack>
  );
};
